import React, { useState, useEffect } from "react";
import { Button, Accordion } from "react-bootstrap";
import ContainerConfigurator from "../grid_configurator/GridConfigurator.jsx";
import IdealStudio from "../../../../core/editor/main";
import "@trendmicro/react-toggle-switch/dist/react-toggle-switch.css";
import { UPDATE_TOOLBAR, UPDATE_BOX } from "../../../../common/actions";
import i18n from "i18next";
import "./_pluginToolbar.scss";
import {
  renderAccordion,
  toolbarMapper,
  toolbarFiller,
} from "../../../../core/editor/accordion_provider";
import PropTypes from "prop-types";
import { blurCKEditor } from "../../../../common/common_tools";
import GuideMultiInputProperties from "../../../../plugins/GuideMultiInput/components/GuideMultiInputProperties";
// === Task 23 ===
import FlowItemsExtension from "./FlowItemsExtension";
import ShapeSettingsSections from "../../shape_settings/ShapeSettingsSections";
// === /Task 23 ===

function getTranslatedContainerName(containerKey, fallbackIndex, savedName) {
  if (!containerKey) return savedName || i18n.t("Block") + " " + fallbackIndex;

  const match = containerKey.match(/^sc-([A-Za-z]+)(\d*)$/);
  if (match) {
    const wordKey = match[1];
    const numberStr = match[2];

    let wordToTranslate = wordKey;
    let translated = i18n.t(wordToTranslate);

    if (numberStr !== "") {
      const displayNum = parseInt(numberStr, 10) + 1;
      return translated + " " + displayNum;
    }

    return translated;
  }

  return savedName || i18n.t("Block") + " " + fallbackIndex;
}

function PluginToolbar(props) {
  const [, setLang] = useState(i18n.language);

  useEffect(() => {
    const handler = (lng) => setLang(lng);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  if (!props.box || !props.box.id) {
    return null;
  }

  let toolbar = props.pluginToolbars[props.box.id];
  if (!toolbar) {
    return null;
  }

  let apiPlugin = IdealStudio.Plugins.get(toolbar.pluginId);
  let config;
  let controls;
  if (apiPlugin) {
    config = apiPlugin.getConfig();
    controls = apiPlugin.getToolbar(toolbar.state, props.box);
  } else {
    config = {};
    controls = {};
  }

  let textButton;
  if (config.needsTextEdition) {
    textButton = (
      <div className="panel-body">
        <Button
          key={"text"}
          className={toolbar.showTextEditor ? "toolbarButton textediting" : "toolbarButton"}
          onClick={() => {
            blurCKEditor(toolbar.id, (text, content) => {
              props.onTextEditorToggled(toolbar.id, !toolbar.showTextEditor, text, content);
            });
          }}
        >
          <i className="toolbarIcons material-icons">mode_edit</i>
          {i18n.t("edit_text")}
        </Button>
      </div>
    );
  }

  let configButton;
  if (config && config.needsConfigModal) {
    configButton = (
      <div className="panel-body">
        <Button
          key={"config"}
          className="toolbarButton"
          onClick={() => {
            props.openConfigModal(toolbar.id);
          }}
        >
          <i className="toolbarIcons material-icons">build</i>
          {i18n.t("open_conf")}
        </Button>
      </div>
    );
  }
  if (apiPlugin) {
    if (props.box.startanimation !== undefined) {
      toolbar.animation = {};
      toolbar.animation.startanimation = props.box.startanimation;
      toolbar.animation.endanimation = props.box.endanimation;
      toolbar.animation.finalanimation = props.box.finalanimation;
      toolbar.animation.middleanimation = props.box.middleanimation;
    }

    if (props.box != null) {
      toolbar.position = props.box.position;
    }

    toolbarFiller(
      controls,
      props.box.id,
      toolbar,
      config,
      config,
      props.box.parent,
      null,
      props.exercises
    );

    controls = toolbarMapper(controls, toolbar);
  } else {
    controls = {
      main: {
        __name: "Main",
        accordions: {},
      },
    };
  }

  if (toolbar && toolbar.pluginId === "GuideMultiInput") {
    return (
      <GuideMultiInputProperties
        boxId={props.box.id}
        state={toolbar.state || {}}
        onToolbarUpdated={props.onToolbarUpdated}
      />
    );
  }

  // === Task 23 === Flow Items extension is appended for InteractivePlugin only.
  const isInteractivePlugin = toolbar && toolbar.pluginId === "InteractivePlugin";
  // === /Task 23 ===

  return Object.keys(controls).map((tabKey, index) => {
    let tab = controls[tabKey];
    let children = props.box.children ? [...props.box.children] : [];
    let collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

    children.sort((a, b) => {
      const getRank = (id) => {
        if (id.includes("Question")) return 1;
        if (id.includes("TrueFeedback")) return 2;
        if (id.includes("FalseFeedback")) return 3;
        if (id.includes("Answer")) return 4;
        return 5;
      };

      const rankA = getRank(a);
      const rankB = getRank(b);

      return rankA !== rankB ? rankA - rankB : collator.compare(a, b);
    });

    return (
      <div key={"key_" + index} className="toolbarTab">
        <Accordion>
          {/* VectorShape: Dolgu … Metin Kutusu sections, above Yapı. */}
          {tabKey === "main" && toolbar.pluginId === "VectorShape" ? (
            <ShapeSettingsSections boxId={props.box.id} state={toolbar.state} />
          ) : null}
          {Object.keys(tab.accordions)
            .sort()
            .map((accordionKey, ind) => {
              if (!tab.accordions[accordionKey]) {
                return null;
              }
              return renderAccordion(
                tab.accordions[accordionKey],
                tabKey,
                [accordionKey],
                controls,
                ind,
                props
              );
            })}
          {children.map((id, ind) => {
            let container = props.box.sortableContainers[id];
            if (tabKey === "main") {
              return (
                <Accordion.Item
                  key={"panel_" + id}
                  eventKey={`container-${id}`}
                  className={"panelPluginToolbar"}
                >
                  <Accordion.Header className={"panel-heading"}>
                    <div className={"titleA"}>
                      {toolbar.state.__pluginContainerIds &&
                      toolbar.state.__pluginContainerIds[container.key] &&
                      container.key
                        ? getTranslatedContainerName(
                            container.key,
                            ind + 1,
                            toolbar.state.__pluginContainerIds[container.key].name
                          )
                        : i18n.t("Block") + " " + (ind + 1)}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ContainerConfigurator
                      id={id}
                      parentId={props.box.id}
                      container={container}
                      onColsChanged={props.onColsChanged}
                      onRowsChanged={props.onRowsChanged}
                      sortableProps={props.box.sortableContainers[id]}
                      onSortablePropsChanged={props.onSortablePropsChanged}
                      onSortableContainerResized={props.onSortableContainerResized}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              );
            }
            return null;
          })}
        </Accordion>
        {textButton}
        {configButton}
        {/* === Task 23 === */}
        {isInteractivePlugin && tabKey === "main" && <FlowItemsExtension boxId={props.box.id} />}
        {/* === /Task 23 === */}
      </div>
    );
  });
}

export default PluginToolbar;
